'use client';

import { useId, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import {
  tx,
  asList,
  useCurrentUser,
  useLearningSource,
  useBlogSource,
  useMediaSource,
  useBlockPart,
  type LearningCategory,
  type BlogCategory,
  type MediaCategory,
} from '@akademi/site-builder-runtime';
import { SectionShell } from '../_shared/SectionShell';
import type { PaddingBox } from '../_shared/resolvePadding';
import { Button } from '@akademi/ui';
import { useAsyncResource } from '../_shared/useAsyncResource';

/**
 * Horizontal scroll of filter pills. Two modes:
 * <ul>
 *   <li><b>Manual</b>: operator supplies {@code pills} array.</li>
 *   <li><b>Adapter-fed</b>: operator picks {@code feedSource} +
 *       {@code baseHref}, the block fetches categories from the
 *       matching content adapter and renders one pill per category.</li>
 * </ul>
 */
export type CategoryFeedSource = 'courses' | 'blog' | 'video';

interface Pill {
  label: string;
  href: string;
  active?: boolean;
}

export interface CategoryFilterPillsProps {
  title?: string;
  subtitle?: string;
  pills?: Pill[];
  /** Adapter-fed mode. */
  feedSource?: CategoryFeedSource;
  /** Base href for adapter-fed pills — {@code {slug}} is replaced
   *  with each category's slug. Default
   *  {@code /catalog?category={slug}}. */
  hrefTemplate?: string;
  /** Limit when feeding from an adapter. Default 20. */
  limit?: number;
  /** Show an "All" pill linking to the base path (template without
   *  the query). Default true in adapter-fed mode. */
  showAllPill?: boolean;
  /**
   * Auth-gate the whole block. {@code 'any'} (default) always renders;
   * {@code 'in'} only for signed-in users; {@code 'out'} only for
   * anonymous visitors.
   */
  requireAuth?: 'any' | 'in' | 'out';
  style?: CSSProperties;
  paddingOuter?: PaddingBox;
  /** Draw the decorative gradient rule beside the section heading. */
  headingLine?: boolean;
  paddingInner?: PaddingBox;
}

function useActiveCategory(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return new URLSearchParams(window.location.search).get('category') ?? undefined;
}

export function CategoryFilterPills({
  title,
  subtitle: _subtitle,
  pills: manualPills,
  feedSource,
  hrefTemplate = '/catalog?category={slug}',
  limit = 20,
  showAllPill = true,
  requireAuth,
  style,
  paddingOuter,
  headingLine,
  paddingInner,
}: CategoryFilterPillsProps) {
  const instanceClass = `lms-filter-pills-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const currentUser = useCurrentUser();
  const learning = useLearningSource();
  const blog = useBlogSource();
  const media = useMediaSource();
  const active = useActiveCategory();

  const fetchedState = useAsyncResource<Pill[]>(async () => {
    if (!feedSource) return null;
    if (feedSource === 'courses') {
      if (!learning) return null;
      const cats = await learning.fetchCategories();
      return cats.length > 0
        ? cats.slice(0, limit).map((c) => learningToPill(c, hrefTemplate, active))
        : null;
    }
    if (feedSource === 'blog') {
      if (!blog) return null;
      const cats = await blog.listCategories();
      return cats.length > 0
        ? cats.slice(0, limit).map((c) => blogToPill(c, hrefTemplate, active))
        : null;
    }
    if (feedSource === 'video') {
      if (!media) return null;
      const cats = await media.categories.list();
      return cats.length > 0
        ? cats.slice(0, limit).map((c) => mediaToPill(c, hrefTemplate, active))
        : null;
    }
    return null;
  }, [feedSource, learning, blog, media, hrefTemplate, limit, active]);

  // Auth gate — mirror the shell's requireAuth semantics.
  const isSignedIn = currentUser != null;
  const gate = requireAuth ?? 'any';
  const hidden = (gate === 'in' && !isSignedIn) || (gate === 'out' && isSignedIn);
  if (hidden) return <></>;

  const list: Pill[] = feedSource
    ? fetchedState.kind === 'loaded'
      ? fetchedState.data
      : []
    : asList<Pill>(manualPills);

  if (list.length === 0 && !feedSource) {
    return <></>;
  }

  // For adapter-fed mode, prepend an "All" pill when configured.
  const allPills: Pill[] =
    feedSource && showAllPill
      ? [
          {
            label: t('discovery.pills.all'),
            href: hrefTemplate.replace(/[?&]category=\{slug\}/, '').replace('?', '') || '/',
            active: !active,
          },
          ...list,
        ]
      : list;

  return (
    <SectionShell
      headingLine={headingLine}
      style={style}
      title={tx(title)}
      paddingOuter={paddingOuter}
      paddingInner={paddingInner}
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `.${instanceClass}::-webkit-scrollbar { display: none; }`,
        }}
      />
      <div
        className={instanceClass}
        {...part('filter')}
        style={{
          display: 'flex',
          gap: 8,
          overflowX: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          paddingBottom: 4,
          scrollSnapType: 'x proximity',
          overscrollBehaviorX: 'contain',
        }}
      >
        {allPills.map((p, i) => (
          <Button
            key={i}
            href={p.href}
            variant={p.active ? 'default' : 'outline'}
            radius="pill"
            {...part('item', i)}
            style={{
              flexShrink: 0,
              padding: 'var(--space-2, 8px) var(--space-4, 16px)',
              fontSize: 14,
              fontWeight: 700,
              // Pin the background so the outline variant's hover swap
              // (-> --color-surface-muted) can't make the fill appear/
              // disappear on hover. Caller style wins last in buildStyle.
              backgroundColor: p.active
                ? 'var(--color-brand-500, #319db3)'
                : 'var(--color-surface-card, #fff)',
              scrollSnapAlign: 'start',
            }}
          >
            {p.label}
          </Button>
        ))}
      </div>
    </SectionShell>
  );
}

function fillTemplate(tpl: string, slug: string): string {
  return tpl.replace('{slug}', encodeURIComponent(slug));
}

function learningToPill(c: LearningCategory, tpl: string, active: string | undefined): Pill {
  const slug = c.slug || c.id;
  return { label: c.name, href: fillTemplate(tpl, slug), active: active === slug };
}
function blogToPill(c: BlogCategory, tpl: string, active: string | undefined): Pill {
  const slug = c.slug || String(c.id);
  return { label: c.name, href: fillTemplate(tpl, slug), active: active === slug };
}
function mediaToPill(c: MediaCategory, tpl: string, active: string | undefined): Pill {
  return { label: c.name, href: fillTemplate(tpl, c.slug), active: active === c.slug };
}
